import { C1Chip, C1ChipProps } from 'c1ui/components/C1Chip'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { FC, useMemo } from 'react'
import { FormattedNumber } from 'react-intl'

type CountDisplayProps = Omit<C1ChipProps, 'label'> & {
    color?: 'primary' | 'warning' | 'info'
    variant?: 'outlined' | 'filled'
    label?: number
}

/**
 * A slightly modified version of C1Chip, intended for displaying an integer count next to a
 * nav or tab label. Restricted to a smaller set of colors and variants.
 */
export const C1CountChip: FC<CountDisplayProps> = ({ color = 'info', variant = 'outlined', label, sx, ...rest }) => {
    const styles = useMemo(() => sxc({
        ...sx,
        fontSize: 9,
        border: 'none',
        borderRadius: '6px',
        px: 1,
        py: '0.15rem',
        ml: 1,
        minHeight: 0,
        height: 'auto',
        backgroundColor: variant === 'filled' ? `${color}.main` : `${color}.extraLight`,
        color: variant === 'filled' ? `${color}.contrastText` : `${color}.main`,
        '.MuiButton-endIcon &': { fontSize: '0.8rem' },
        '& .MuiChip-label': { px: '0rem' },
        '.Mui-selected &': {
            color: 'primary.main',
            backgroundColor: 'primary.extraLight',
        },
        '.MuiTab-root:hover &': {
            color: 'primary.dark',
            backgroundColor: 'primary.light',
        },
        '.MuiTab-root:focus-visible &': {
            color: 'primary.dark',
            backgroundColor: 'primary.light',
        },
    }), [sx, variant, color])

    return label !== undefined && label > 0 && <C1Chip
        component="span"
        label={<FormattedNumber value={label} />}
        sx={styles}
        {...rest}
    />
}
